import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
} from "d3";
import ELK from "elkjs/lib/elk.bundled.js";
import { useRef, useState, type Dispatch, type SetStateAction } from "react";
import { type Edge, type Node } from "reactflow";

const NODE_W = 160;
const NODE_H = 60;

type Direction = "TB" | "BT" | "LR" | "RL";
type Algo = "elk" | "fd";
type Pos = { x: number; y: number };

type EdgeOverride = {
  sourceHandle?: string | null;
  targetHandle?: string | null;
  lineType: string;
  positionHandlers?: { x: number; y: number; active: number }[];
};

type LayoutSnapshot = {
  nodes: { id: string; position: Pos }[];
  edges: {
    id: string;
    positionHandlers: any[];
    lineType?: string;
    sourceHandle?: string | null;
    targetHandle?: string | null;
  }[];
};

type EdgePair = { source: string; target: string };
type SimNode = { id: string; x: number; y: number };

const HANDLE_MAP: Record<Direction, { src: string; tgt: string }> = {
  TB: { src: "bottom", tgt: "top" },
  BT: { src: "top", tgt: "bottom" },
  LR: { src: "right", tgt: "left" },
  RL: { src: "left", tgt: "right" },
};

// Lado do nó (handle do React Flow) apontado pelo vetor (dx, dy) a partir do centro
function sideFromDelta(dx: number, dy: number): string {
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? "right" : "left";
  return dy >= 0 ? "bottom" : "top";
}

// --- crossing minimization helpers ---

function segmentsIntersect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): boolean {
  const d1x = bx - ax,
    d1y = by - ay;
  const d2x = dx - cx,
    d2y = dy - cy;
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return false;
  const t = ((cx - ax) * d2y - (cy - ay) * d2x) / cross;
  const u = ((cx - ax) * d1y - (cy - ay) * d1x) / cross;
  return t > 1e-6 && t < 1 - 1e-6 && u > 1e-6 && u < 1 - 1e-6;
}

// Count crossing pairs that involve at least one edge incident to u or v.
// Swapping u↔v only affects these pairs, so comparing before/after gives the delta.
function crossingsInvolvingNodes(
  posMap: Map<string, Pos>,
  edgeList: EdgePair[],
  u: string,
  v: string,
): number {
  let count = 0;
  for (let i = 0; i < edgeList.length; i++) {
    for (let j = i + 1; j < edgeList.length; j++) {
      const a = edgeList[i],
        b = edgeList[j];
      const aTouch =
        a.source === u || a.target === u || a.source === v || a.target === v;
      const bTouch =
        b.source === u || b.target === u || b.source === v || b.target === v;
      if (!aTouch && !bTouch) continue;
      if (
        a.source === b.source ||
        a.source === b.target ||
        a.target === b.source ||
        a.target === b.target
      )
        continue;
      const s1 = posMap.get(a.source),
        t1 = posMap.get(a.target);
      const s2 = posMap.get(b.source),
        t2 = posMap.get(b.target);
      if (!s1 || !t1 || !s2 || !t2) continue;
      if (segmentsIntersect(s1.x, s1.y, t1.x, t1.y, s2.x, s2.y, t2.x, t2.y))
        count++;
    }
  }
  return count;
}

// Total de cruzamentos entre todos os pares de enlaces (ignora enlaces que
// compartilham um nó). Usado para comparar candidatos do multi-start.
function countAllCrossings(
  posMap: Map<string, Pos>,
  edgeList: EdgePair[],
): number {
  let count = 0;
  for (let i = 0; i < edgeList.length; i++) {
    for (let j = i + 1; j < edgeList.length; j++) {
      const a = edgeList[i],
        b = edgeList[j];
      if (
        a.source === b.source ||
        a.source === b.target ||
        a.target === b.source ||
        a.target === b.target
      )
        continue;
      const s1 = posMap.get(a.source),
        t1 = posMap.get(a.target);
      const s2 = posMap.get(b.source),
        t2 = posMap.get(b.target);
      if (!s1 || !t1 || !s2 || !t2) continue;
      if (segmentsIntersect(s1.x, s1.y, t1.x, t1.y, s2.x, s2.y, t2.x, t2.y))
        count++;
    }
  }
  return count;
}

function buildNeighbors(edgeList: EdgePair[]): Map<string, string[]> {
  const neighbors = new Map<string, string[]>();
  edgeList.forEach(({ source, target }) => {
    if (!neighbors.has(source)) neighbors.set(source, []);
    if (!neighbors.has(target)) neighbors.set(target, []);
    neighbors.get(source)!.push(target);
    neighbors.get(target)!.push(source);
  });
  return neighbors;
}

// Hill-climbing: try all pairs of node position swaps and keep any that reduce crossings.
// Skipped for large graphs (> 100 nodes) to stay fast.
function minimizeCrossings(
  posMap: Map<string, Pos>,
  edgeList: EdgePair[],
  maxPasses = 3,
): void {
  const ids = Array.from(posMap.keys());
  if (ids.length > 100 || edgeList.length === 0) return;
  for (let pass = 0; pass < maxPasses; pass++) {
    let improved = false;
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const u = ids[i],
          v = ids[j];
        const before = crossingsInvolvingNodes(posMap, edgeList, u, v);
        if (before === 0) continue;
        const pu = posMap.get(u)!;
        const pv = posMap.get(v)!;
        posMap.set(u, pv);
        posMap.set(v, pu);
        const after = crossingsInvolvingNodes(posMap, edgeList, u, v);
        if (after < before) {
          improved = true;
        } else {
          posMap.set(u, pu);
          posMap.set(v, pv);
        }
      }
    }
    if (!improved) break;
  }
}

// Complementa as trocas de par: tenta REPOSICIONAR nós individualmente em volta
// do baricentro dos vizinhos. Resolve o caso comum de um nó folha que ficou "do
// lado errado" do grafo — cruzamento que nenhuma troca de posições desfaz.
function refineNodePlacement(
  posMap: Map<string, Pos>,
  edgeList: EdgePair[],
  linkDist: number,
  minSep: number = Math.max(NODE_W, NODE_H),
  maxPasses = 2,
): void {
  const ids = Array.from(posMap.keys());
  if (ids.length > 100 || edgeList.length === 0) return;
  const neighbors = buildNeighbors(edgeList);

  for (let pass = 0; pass < maxPasses; pass++) {
    let improved = false;
    for (const u of ids) {
      const before = crossingsInvolvingNodes(posMap, edgeList, u, u);
      if (before === 0) continue;
      const nbrs = neighbors.get(u);
      if (!nbrs?.length) continue;

      let bx = 0,
        by = 0;
      nbrs.forEach((v) => {
        const p = posMap.get(v);
        if (p) {
          bx += p.x;
          by += p.y;
        }
      });
      bx /= nbrs.length;
      by /= nbrs.length;

      const original = posMap.get(u)!;
      const candidates: Pos[] = [{ x: bx, y: by }];
      for (const r of [linkDist, linkDist * 2]) {
        for (let k = 0; k < 8; k++) {
          const angle = (k / 8) * 2 * Math.PI;
          candidates.push({
            x: bx + r * Math.cos(angle),
            y: by + r * Math.sin(angle),
          });
        }
      }

      let bestPos = original;
      let bestCount = before;
      for (const cand of candidates) {
        let overlaps = false;
        for (const [otherId, otherPos] of posMap) {
          if (otherId === u) continue;
          if (Math.hypot(otherPos.x - cand.x, otherPos.y - cand.y) < minSep) {
            overlaps = true;
            break;
          }
        }
        if (overlaps) continue;
        posMap.set(u, cand);
        const after = crossingsInvolvingNodes(posMap, edgeList, u, u);
        if (after < bestCount) {
          bestCount = after;
          bestPos = cand;
        }
      }
      posMap.set(u, bestPos);
      if (bestCount < before) improved = true;
    }
    if (!improved) break;
  }
}

// Semente radial: BFS a partir do nó de maior grau, com cada nível em um anel
// concêntrico. Layouts em árvore quase não têm cruzamentos, então a simulação
// parte de um estado já desembaraçado em vez de herdar o emaranhado atual.
function radialSeed(
  nodeIds: string[],
  edgeList: EdgePair[],
  spacing: number,
): Map<string, Pos> {
  const neighbors = buildNeighbors(edgeList);
  const sorted = [...nodeIds].sort(
    (a, b) => (neighbors.get(b)?.length ?? 0) - (neighbors.get(a)?.length ?? 0),
  );
  const visited = new Set<string>();
  const pos = new Map<string, Pos>();
  const ringGap = spacing * 1.5;
  let offsetX = 0;

  for (const root of sorted) {
    if (visited.has(root)) continue;
    const levels: string[][] = [[root]];
    visited.add(root);
    while (true) {
      const next: string[] = [];
      for (const u of levels[levels.length - 1]) {
        for (const v of neighbors.get(u) ?? []) {
          if (!visited.has(v)) {
            visited.add(v);
            next.push(v);
          }
        }
      }
      if (!next.length) break;
      levels.push(next);
    }
    levels.forEach((level, li) => {
      const r = li * ringGap;
      level.forEach((id, i) => {
        const angle = (i / level.length) * 2 * Math.PI + li * 0.5;
        pos.set(id, {
          x: offsetX + r * Math.cos(angle),
          y: r * Math.sin(angle),
        });
      });
    });
    offsetX += 2 * (levels.length - 1) * ringGap + spacing * 3;
  }
  return pos;
}

// Semente circular: nós em ordem de BFS distribuídos em um círculo único.
// Vizinhos ficam adjacentes no perímetro, outro ponto de partida com poucos
// cruzamentos para o multi-start.
function circleSeed(
  nodeIds: string[],
  edgeList: EdgePair[],
  spacing: number,
): Map<string, Pos> {
  const neighbors = buildNeighbors(edgeList);
  const sorted = [...nodeIds].sort(
    (a, b) => (neighbors.get(b)?.length ?? 0) - (neighbors.get(a)?.length ?? 0),
  );
  const visited = new Set<string>();
  const order: string[] = [];
  for (const root of sorted) {
    if (visited.has(root)) continue;
    visited.add(root);
    const queue = [root];
    while (queue.length) {
      const u = queue.shift()!;
      order.push(u);
      for (const v of neighbors.get(u) ?? []) {
        if (!visited.has(v)) {
          visited.add(v);
          queue.push(v);
        }
      }
    }
  }
  const radius = Math.max(spacing, (order.length * spacing) / (2 * Math.PI));
  const pos = new Map<string, Pos>();
  order.forEach((id, i) => {
    const angle = (i / order.length) * 2 * Math.PI;
    pos.set(id, { x: radius * Math.cos(angle), y: radius * Math.sin(angle) });
  });
  return pos;
}

// --- hook ---

export function useAutoLayout(
  nodes: Node[],
  edges: Edge[],
  setNodes: Dispatch<SetStateAction<Node[]>>,
  setEdges: Dispatch<SetStateAction<Edge[]>>,
  fitView: (options?: { padding?: number; duration?: number }) => void,
  setCheckChanges?: Dispatch<SetStateAction<boolean>>,
) {
  const [preLayoutSnapshot, setPreLayoutSnapshot] =
    useState<LayoutSnapshot | null>(null);
  const layoutPositionsRef = useRef<Map<string, Pos>>(new Map());
  const layoutEdgesRef = useRef<Map<string, EdgeOverride>>(new Map());

  const saveSnapshot = () => {
    if (preLayoutSnapshot) return;
    setPreLayoutSnapshot({
      nodes: nodes.map((n) => ({ id: n.id, position: { ...n.position } })),
      edges: edges.map((e) => ({
        id: e.id,
        positionHandlers: structuredClone(
          (e.data as any)?.positionHandlers ?? [],
        ),
        lineType: (e.data as any)?.lineType,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
      })),
    });
  };

  const applyElkLayout = async (
    dir: Direction,
    nodesep: number,
    ranksep: number,
    edgesep: number,
    portsep: number = 20,
  ) => {
    const elkDirMap: Record<Direction, string> = {
      TB: "DOWN",
      BT: "UP",
      LR: "RIGHT",
      RL: "LEFT",
    };
    const elk = new (ELK as any)();

    const handles = HANDLE_MAP[dir];

    const graph = {
      id: "root",
      layoutOptions: {
        "elk.algorithm": "layered",
        "elk.direction": elkDirMap[dir],
        "elk.spacing.nodeNode": String(nodesep),
        "elk.edgeRouting": "ORTHOGONAL",
        // FREE: o ELK distribui múltiplas arestas pelo lado do nó (evita sobreposição).
        // Não usamos os IDs de porta que o ELK devolve — só os bendPoints.
        portConstraints: "FREE",
        "elk.layered.spacing.edgeEdgeBetweenLayers": String(edgesep),
        "elk.layered.spacing.nodeNodeBetweenLayers": String(ranksep),
        // Mais varreduras de minimização de cruzamentos (padrão é 7) + passe
        // greedy-switch bilateral: menos enlaces cruzados ao custo de alguns ms.
        "elk.layered.thoroughness": "30",
        "elk.layered.crossingMinimization.greedySwitch.type": "TWO_SIDED",
        // Posiciona nós minimizando dobras/comprimento dos enlaces (linhas mais retas)
        "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
        // Afasta enlaces de nós que não participam deles
        "elk.spacing.edgeNode": String(Math.max(edgesep * 2, 20)),
        "elk.spacing.edgeEdge": String(edgesep),
        // Componentes desconexos (e nós isolados) são organizados em blocos
        // compactos lado a lado, em vez de entrarem todos na mesma camada.
        "elk.separateConnectedComponents": "true",
        "elk.spacing.componentComponent": String(nodesep * 2),
        // Respeita a ordem dos nós/enlaces do modelo em empates: layouts
        // estáveis entre execuções, sem flips arbitrários.
        "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      },

      // Usa o tamanho real medido pelo React Flow quando disponível — nós fora
      // do padrão (rótulos longos, ícones) deixam de invadir o espaçamento.
      children: nodes.map((n) => ({
        id: n.id,
        width: n.width ?? NODE_W,
        height: n.height ?? NODE_H,
      })),

      edges: edges
        .filter(
          (e) =>
            nodes.some((n) => n.id === e.source) &&
            nodes.some((n) => n.id === e.target),
        )
        .map((e) => ({
          id: e.id,
          sources: [e.source],
          targets: [e.target],
        })),
    };

    const layout = await elk.layout(graph);
    const posMap = new Map<string, Pos>(
      (layout.children ?? []).map((n: any) => [
        n.id,
        { x: n.x ?? 0, y: n.y ?? 0 },
      ]),
    );

    const newPositions = new Map<string, Pos>();

    // Atualiza a posição dos nós calculada pelo ELK
    setNodes((prev) =>
      prev.map((node) => {
        const pos = posMap.get(node.id);
        if (!pos) return node;
        newPositions.set(node.id, pos);
        return { ...node, position: pos };
      }),
    );
    layoutPositionsRef.current = newPositions;

    const newEdgeOverrides = new Map<string, EdgeOverride>();
    const elkEdgesMap = new Map<string, any>(
      layout.edges?.map((e: any) => [e.id, e]) ?? [],
    );
    const sizeMap = new Map<string, { w: number; h: number }>(
      nodes.map((n) => [n.id, { w: n.width ?? NODE_W, h: n.height ?? NODE_H }]),
    );

    const sideOfPoint = (
      pt: { x: number; y: number },
      pos: Pos,
      size: { w: number; h: number },
    ) => sideFromDelta(pt.x - (pos.x + size.w / 2), pt.y - (pos.y + size.h / 2));

    // --- Reespaçamento das saídas ---
    // O ELK espalha as portas pelo lado do nó com espaçamento interno próprio,
    // não controlável de forma confiável com portas livres. Reagrupamos os
    // pontos de saída/chegada por (nó, lado), preservamos a ordem do ELK (não
    // cria cruzamentos novos) e redistribuímos com o passo `portsep`, centrado
    // no meio do lado — é isso que controla a distância entre as linhas
    // paralelas na saída do nó.
    const edgeEndpoints = new Map(
      edges.map((e) => [e.id, { source: e.source, target: e.target }]),
    );
    const exitGroups = new Map<
      string,
      { edgeId: string; end: "start" | "end"; along: number }[]
    >();

    for (const [edgeId, elkEdge] of elkEdgesMap) {
      const section = elkEdge?.sections?.[0];
      const ep = edgeEndpoints.get(edgeId);
      if (!section || !ep) continue;
      const collect = (
        pt: { x: number; y: number } | undefined,
        nodeId: string,
        end: "start" | "end",
      ) => {
        if (!pt) return;
        const pos = posMap.get(nodeId);
        const size = sizeMap.get(nodeId);
        if (!pos || !size) return;
        const side = sideOfPoint(pt, pos, size);
        const horizontal = side === "top" || side === "bottom";
        const key = `${nodeId}#${side}`;
        if (!exitGroups.has(key)) exitGroups.set(key, []);
        exitGroups.get(key)!.push({
          edgeId,
          end,
          along: horizontal ? pt.x : pt.y,
        });
      };
      collect(section.startPoint, ep.source, "start");
      collect(section.endPoint, ep.target, "end");
    }

    // `${edgeId}#start|end` → ponto reespaçado sobre a borda do nó
    const exitOverride = new Map<string, Pos>();
    for (const [key, refs] of exitGroups) {
      const sepIdx = key.lastIndexOf("#");
      const nodeId = key.slice(0, sepIdx);
      const side = key.slice(sepIdx + 1);
      const pos = posMap.get(nodeId);
      const size = sizeMap.get(nodeId);
      if (!pos || !size) continue;
      refs.sort((r1, r2) => r1.along - r2.along);
      const horizontal = side === "top" || side === "bottom";
      const centerAlong = horizontal ? pos.x + size.w / 2 : pos.y + size.h / 2;
      const fixed =
        side === "top"
          ? pos.y
          : side === "bottom"
            ? pos.y + size.h
            : side === "left"
              ? pos.x
              : pos.x + size.w;
      refs.forEach((r, i) => {
        const along = centerAlong + (i - (refs.length - 1) / 2) * portsep;
        exitOverride.set(
          `${r.edgeId}#${r.end}`,
          horizontal ? { x: along, y: fixed } : { x: fixed, y: along },
        );
      });
    }

    // Derivamos os handles do React Flow da rota real calculada pelo ELK: o
    // ponto onde o enlace sai/entra do nó indica o lado correto. Assim enlaces
    // "flat" (mesma camada) roteados pela lateral deixam de ser forçados a
    // sair por baixo/cima, o que criava dobras contra os bendPoints.
    setEdges((prev) =>
      prev.map((edge) => {
        const elkEdge = elkEdgesMap.get(edge.id);
        const section = elkEdge?.sections?.[0];

        // HANDLE_MAP é o fallback quando o ELK não devolve a rota do enlace
        let chosenSourceHandle = handles.src;
        let chosenTargetHandle = handles.tgt;

        const sPos = posMap.get(edge.source);
        const tPos = posMap.get(edge.target);
        const sSize = sizeMap.get(edge.source);
        const tSize = sizeMap.get(edge.target);
        if (section?.startPoint && sPos && sSize) {
          chosenSourceHandle = sideFromDelta(
            section.startPoint.x - (sPos.x + sSize.w / 2),
            section.startPoint.y - (sPos.y + sSize.h / 2),
          );
        }
        if (section?.endPoint && tPos && tSize) {
          chosenTargetHandle = sideFromDelta(
            section.endPoint.x - (tPos.x + tSize.w / 2),
            section.endPoint.y - (tPos.y + tSize.h / 2),
          );
        }

        // Bifurcação já na saída do nó: o ELK distribui as portas ao longo do
        // lado do nó, mas o React Flow desenha todos os enlaces a partir do
        // MESMO handle central — sem o ponto de porta real, o primeiro
        // segmento vira uma diagonal longa até o primeiro bendPoint e enlaces
        // paralelos formam "agulhas" que só se separam no meio do caminho.
        // Incluímos os pontos de saída/chegada do ELK (empurrados para fora
        // do nó, senão o segmento correria colado na borda) como primeiro e
        // último handler.
        const OUT_GAP = 14;
        const outward = (pt: { x: number; y: number }, side: string) => ({
          x: pt.x + (side === "left" ? -OUT_GAP : side === "right" ? OUT_GAP : 0),
          y: pt.y + (side === "top" ? -OUT_GAP : side === "bottom" ? OUT_GAP : 0),
          active: 0,
        });
        const anchorOf = (
          pos: Pos,
          size: { w: number; h: number },
          side: string,
        ): Pos => ({
          x:
            side === "left"
              ? pos.x
              : side === "right"
                ? pos.x + size.w
                : pos.x + size.w / 2,
          y:
            side === "top"
              ? pos.y
              : side === "bottom"
                ? pos.y + size.h
                : pos.y + size.h / 2,
        });

        const bendPoints = section?.bendPoints ?? [];
        const positionHandlers: { x: number; y: number; active: number }[] =
          [];

        // Ponto reespaçado pelo passo do usuário; cai no original do ELK se
        // o reespaçamento não cobriu este enlace
        const startPt =
          exitOverride.get(`${edge.id}#start`) ?? section?.startPoint;
        const endPt = exitOverride.get(`${edge.id}#end`) ?? section?.endPoint;

        // O reespaçamento da porta precisa PROPAGAR para o trecho reto que
        // sai dela: os bendPoints iniciais/finais alinhados com a porta
        // original (mesmo x para saídas verticais, mesmo y para laterais)
        // acompanham o deslocamento — senão a linha abre na saída e volta a
        // afunilar na coluna antiga do ELK logo abaixo do nó.
        const adjustedBends: { x: number; y: number }[] = bendPoints.map(
          (pt: any) => ({
            x: pt.x,
            y: pt.y,
          }),
        );
        const shiftRun = (
          fromStart: boolean,
          origPt: { x: number; y: number } | undefined,
          newPt: Pos | undefined,
          side: string,
        ) => {
          if (!origPt || !newPt) return;
          const axis: "x" | "y" =
            side === "top" || side === "bottom" ? "x" : "y";
          const delta = newPt[axis] - origPt[axis];
          if (Math.abs(delta) < 0.5) return;
          for (let k = 0; k < adjustedBends.length; k++) {
            const i = fromStart ? k : adjustedBends.length - 1 - k;
            if (Math.abs(adjustedBends[i][axis] - origPt[axis]) > 1) break;
            adjustedBends[i][axis] += delta;
          }
        };
        shiftRun(
          true,
          section?.startPoint,
          exitOverride.get(`${edge.id}#start`),
          chosenSourceHandle,
        );
        shiftRun(
          false,
          section?.endPoint,
          exitOverride.get(`${edge.id}#end`),
          chosenTargetHandle,
        );

        if (startPt && sPos && sSize) {
          const anchor = anchorOf(sPos, sSize, chosenSourceHandle);
          // Só vale a pena quando a porta não coincide com o handle
          const away = Math.hypot(startPt.x - anchor.x, startPt.y - anchor.y);
          if (away > 4)
            positionHandlers.push(outward(startPt, chosenSourceHandle));
        }

        adjustedBends.forEach((pt) =>
          positionHandlers.push({ x: pt.x, y: pt.y, active: 0 }),
        );

        if (endPt && tPos && tSize) {
          const anchor = anchorOf(tPos, tSize, chosenTargetHandle);
          const away = Math.hypot(endPt.x - anchor.x, endPt.y - anchor.y);
          if (away > 4)
            positionHandlers.push(outward(endPt, chosenTargetHandle));
        }

        newEdgeOverrides.set(edge.id, {
          sourceHandle: chosenSourceHandle,
          targetHandle: chosenTargetHandle,
          lineType: "reta",
          positionHandlers,
        });

        return {
          ...edge,
          sourceHandle: chosenSourceHandle,
          targetHandle: chosenTargetHandle,
          data: {
            ...edge.data,
            lineType: "reta",
            positionHandlers,
          },
        };
      }),
    );
    layoutEdgesRef.current = newEdgeOverrides;

    setTimeout(() => fitView({ padding: 0.12, duration: 350 }), 60);
  };

  const applyForceDirectedLayout = (
    linkDist: number,
    repulsionBase: number,
  ) => {
    const nodeSet = new Set(nodes.map((n) => n.id));
    // Preserve string IDs before forceLink mutates source/target to node objects
    const edgePairs: EdgePair[] = edges
      .filter((e) => nodeSet.has(e.source) && nodeSet.has(e.target))
      .map((e) => ({ source: e.source, target: e.target }));

    // Degree-adaptive link strength: high-degree nodes attract less, spreading the graph
    const degreeMap = new Map<string, number>();
    edgePairs.forEach(({ source, target }) => {
      degreeMap.set(source, (degreeMap.get(source) ?? 0) + 1);
      degreeMap.set(target, (degreeMap.get(target) ?? 0) + 1);
    });

    // Exclude isolated nodes from the simulation: without a link force to anchor them,
    // forceManyBody pushes them far away and distorts the connected component.
    const connectedNodes = nodes.filter((n) => degreeMap.has(n.id));
    const isolatedNodes = nodes.filter((n) => !degreeMap.has(n.id));

    // Dimensões reais medidas pelo React Flow: a visão detalhada (e o "size"
    // por elemento) gera nós bem maiores que o fallback 160×60 — assumir o
    // tamanho fixo faz nós grandes se sobreporem e centros ficarem errados.
    const dims = new Map<string, { w: number; h: number }>(
      nodes.map((n) => [n.id, { w: n.width ?? NODE_W, h: n.height ?? NODE_H }]),
    );
    const dimOf = (id: string) => dims.get(id) ?? { w: NODE_W, h: NODE_H };
    let maxDim = NODE_H;
    dims.forEach((d) => {
      maxDim = Math.max(maxDim, d.w, d.h);
    });
    // A distância de link nunca pode ser menor que o maior nó: senão a força
    // de atração briga com a de colisão e o layout fica apertado e instável.
    const effectiveDist = Math.max(linkDist, maxDim * 1.1);

    const runSimulation = (seed: Map<string, Pos>): Map<string, Pos> => {
      const simNodes: SimNode[] = connectedNodes.map((n) => ({
        id: n.id,
        x: seed.get(n.id)?.x ?? 0,
        y: seed.get(n.id)?.y ?? 0,
      }));
      const simEdges = edgePairs.map((e) => ({ ...e }));

      const simulation = forceSimulation<SimNode>(simNodes)
        .force(
          "link",
          forceLink<SimNode, (typeof simEdges)[number]>(simEdges)
            .id((d) => d.id)
            .distance(effectiveDist)
            .strength((link: any) => {
              const srcId: string =
                typeof link.source === "object" ? link.source.id : link.source;
              const tgtId: string =
                typeof link.target === "object" ? link.target.id : link.target;
              const maxDeg = Math.max(
                degreeMap.get(srcId) ?? 1,
                degreeMap.get(tgtId) ?? 1,
              );
              return 1 / Math.sqrt(maxDeg);
            }),
        )
        .force(
          "charge",
          forceManyBody<SimNode>()
            .strength(-Math.max(repulsionBase * 3, effectiveDist * 8))
            // Limita o alcance da repulsão: sem isso, grupos fracamente conectados
            // continuam se afastando ao longo das iterações, já que não há força
            // de atração equivalente entre eles para compensar.
            .distanceMax(effectiveDist * 4),
        )
        .force("center", forceCenter<SimNode>(0, 0))
        .force(
          "collide",
          // Raio de colisão por nó: nós da visão detalhada (maiores) ganham
          // mais espaço em vez de usar o raio do tamanho padrão para todos.
          forceCollide<SimNode>().radius((d) => {
            const { w, h } = dimOf(d.id);
            return Math.max(w, h) * 0.75 + 10;
          }),
        )
        .alphaDecay(0.007);

      simulation.stop();
      for (let i = 0; i < 600; i++) simulation.tick();

      return new Map<string, Pos>(
        simNodes.map((n) => [n.id, { x: n.x ?? 0, y: n.y ?? 0 }]),
      );
    };

    // Multi-start: a simulação para em mínimos locais que dependem das posições
    // iniciais — se o grafo começa emaranhado, termina emaranhado. Rodamos a
    // partir de sementes diferentes e ficamos com o resultado de menos
    // cruzamentos. Grafos grandes usam só a semente atual para não travar a UI.
    const currentSeed = new Map<string, Pos>(
      connectedNodes.map((n) => {
        const { w, h } = dimOf(n.id);
        return [n.id, { x: n.position.x + w / 2, y: n.position.y + h / 2 }];
      }),
    );
    const connectedIds = connectedNodes.map((n) => n.id);
    const seeds: Map<string, Pos>[] = [currentSeed];
    if (connectedNodes.length <= 150) {
      seeds.push(radialSeed(connectedIds, edgePairs, effectiveDist));
      seeds.push(circleSeed(connectedIds, edgePairs, effectiveDist));
    }

    let posMap = currentSeed;
    let bestCrossings = Infinity;
    for (const seed of seeds) {
      const candidate = runSimulation(seed);
      minimizeCrossings(candidate, edgePairs);
      refineNodePlacement(candidate, edgePairs, effectiveDist, maxDim);
      minimizeCrossings(candidate, edgePairs);
      const crossings = countAllCrossings(candidate, edgePairs);
      if (crossings < bestCrossings) {
        bestCrossings = crossings;
        posMap = candidate;
      }
      if (bestCrossings === 0) break;
    }

    // Place isolated nodes in a ring around the connected component
    if (isolatedNodes.length > 0) {
      const connectedPositions = Array.from(posMap.values());
      const cx =
        connectedPositions.length > 0
          ? connectedPositions.reduce((s, p) => s + p.x, 0) /
            connectedPositions.length
          : 0;
      const cy =
        connectedPositions.length > 0
          ? connectedPositions.reduce((s, p) => s + p.y, 0) /
            connectedPositions.length
          : 0;
      const maxR =
        connectedPositions.length > 0
          ? connectedPositions.reduce(
              (r, p) => Math.max(r, Math.hypot(p.x - cx, p.y - cy)),
              0,
            )
          : 0;
      // Raio mínimo para o perímetro comportar todos os isolados sem sobreposição
      const ringR = (isolatedNodes.length * maxDim * 1.3) / (2 * Math.PI);
      const orbitR = Math.max(maxR + repulsionBase, ringR);
      const angleStep = (2 * Math.PI) / isolatedNodes.length;
      isolatedNodes.forEach((node, i) => {
        const angle = i * angleStep - Math.PI / 2;
        posMap.set(node.id, {
          x: cx + orbitR * Math.cos(angle),
          y: cy + orbitR * Math.sin(angle),
        });
      });
    }

    const newPositions = new Map<string, Pos>();
    setNodes((prev) =>
      prev.map((node) => {
        const c = posMap.get(node.id);
        if (!c) return node;
        const { w, h } = dimOf(node.id);
        const position = { x: c.x - w / 2, y: c.y - h / 2 };
        newPositions.set(node.id, position);
        return { ...node, position };
      }),
    );
    layoutPositionsRef.current = newPositions;

    const OPPOSITE: Record<string, string> = {
      right: "left",
      left: "right",
      bottom: "top",
      top: "bottom",
    };
    const bestHandles = (srcId: string, tgtId: string) => {
      const s = posMap.get(srcId);
      const t = posMap.get(tgtId);
      if (!s || !t)
        return { src: null as string | null, tgt: null as string | null };
      const src = sideFromDelta(t.x - s.x, t.y - s.y);
      return { src, tgt: OPPOSITE[src] };
    };

    // Enlaces paralelos (mesmo par de nós) ficariam exatamente sobrepostos em
    // linha reta. Cada um ganha UM handler no ponto médio com deslocamento
    // perpendicular distinto. Em vez de centrar o leque cegamente, os
    // deslocamentos candidatos dos DOIS lados do corredor são pontuados por
    // quantos cruzamentos o arco geraria com os demais enlaces — se algo já
    // passa por um lado, o leque desloca para o lado livre.
    const FAN_GAP = 60;
    const pairGroups = new Map<string, string[]>();
    const edgeInfos = edges
      .filter((e) => nodeSet.has(e.source) && nodeSet.has(e.target))
      .map((e) => ({ id: e.id, source: e.source, target: e.target }));
    edgeInfos.forEach((e) => {
      const key =
        e.source < e.target
          ? `${e.source}|${e.target}`
          : `${e.target}|${e.source}`;
      if (!pairGroups.has(key)) pairGroups.set(key, []);
      pairGroups.get(key)!.push(e.id);
    });

    const handlerByEdge = new Map<
      string,
      { x: number; y: number; active: number }
    >();

    // Segmento (p1→p2) cruza o enlace? Usa a polilinha com o handler já
    // atribuído, para grupos processados antes contarem com a geometria real.
    const segCrossesEdge = (
      p1: Pos,
      p2: Pos,
      other: { id: string; source: string; target: string },
    ): boolean => {
      const s = posMap.get(other.source);
      const t = posMap.get(other.target);
      if (!s || !t) return false;
      const h = handlerByEdge.get(other.id);
      const pts = h ? [s, h, t] : [s, t];
      for (let i = 0; i < pts.length - 1; i++) {
        if (
          segmentsIntersect(
            p1.x,
            p1.y,
            p2.x,
            p2.y,
            pts[i].x,
            pts[i].y,
            pts[i + 1].x,
            pts[i + 1].y,
          )
        )
          return true;
      }
      return false;
    };

    for (const [key, group] of pairGroups) {
      if (group.length < 2) continue;
      const [aId, bId] = key.split("|");
      const a = posMap.get(aId);
      const b = posMap.get(bId);
      if (!a || !b) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const px = -dy / len;
      const py = dx / len;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const groupSet = new Set(group);

      // Candidatos: 0, ±FAN_GAP, ±2·FAN_GAP... (alguns além do necessário,
      // para haver escolha quando um lado está congestionado)
      const offsets: number[] = [0];
      for (let k = 1; offsets.length < group.length + 4; k++) {
        offsets.push(k * FAN_GAP, -k * FAN_GAP);
      }

      const scored = offsets.map((off) => {
        const h = { x: mid.x + px * off, y: mid.y + py * off };
        let cost = 0;
        for (const other of edgeInfos) {
          if (groupSet.has(other.id)) continue;
          if (segCrossesEdge(a, h, other)) cost++;
          if (segCrossesEdge(h, b, other)) cost++;
        }
        return { off, h, cost };
      });
      // Menor custo primeiro; empate favorece o menor desvio do centro
      scored.sort(
        (s1, s2) => s1.cost - s2.cost || Math.abs(s1.off) - Math.abs(s2.off),
      );
      const chosen = scored
        .slice(0, group.length)
        .sort((s1, s2) => s1.off - s2.off);
      group.forEach((edgeId, i) => {
        handlerByEdge.set(edgeId, { ...chosen[i].h, active: 0 });
      });
    }

    const newEdgeOverrides = new Map<string, EdgeOverride>();
    setEdges((prev) =>
      prev.map((edge) => {
        const { src, tgt } = bestHandles(edge.source, edge.target);
        const handler = handlerByEdge.get(edge.id);
        const positionHandlers = handler ? [handler] : [];
        newEdgeOverrides.set(edge.id, {
          sourceHandle: src,
          targetHandle: tgt,
          lineType: "reta",
          positionHandlers,
        });
        return {
          ...edge,
          sourceHandle: src,
          targetHandle: tgt,
          data: edge.data
            ? { ...edge.data, positionHandlers, lineType: "reta" }
            : edge.data,
        };
      }),
    );
    layoutEdgesRef.current = newEdgeOverrides;

    setTimeout(() => fitView({ padding: 0.12, duration: 350 }), 60);
  };

  const handleAutoLayout = (
    algo: Algo = "elk",
    dir: Direction | null = "TB",
    nodesep: number = 80,
    ranksep: number = 120,
    edgesep: number = 10,
    portsep: number = 20,
  ) => {
    saveSnapshot();
    const direction = (dir ?? "TB") as Direction;
    if (algo === "fd") {
      applyForceDirectedLayout(nodesep * 2, ranksep);
    } else {
      applyElkLayout(direction, nodesep, ranksep, edgesep, portsep);
    }
    setCheckChanges?.(true);
  };

  const handleRevertLayout = () => {
    if (!preLayoutSnapshot) return;
    layoutPositionsRef.current = new Map();
    layoutEdgesRef.current = new Map();
    setNodes((prev) =>
      prev.map((node) => {
        const saved = preLayoutSnapshot.nodes.find((p) => p.id === node.id);
        return saved ? { ...node, position: saved.position } : node;
      }),
    );
    setEdges((prev) =>
      prev.map((edge) => {
        const saved = preLayoutSnapshot.edges.find((e) => e.id === edge.id);
        if (!saved) return edge;
        return {
          ...edge,
          sourceHandle: saved.sourceHandle,
          targetHandle: saved.targetHandle,
          data: edge.data
            ? {
                ...edge.data,
                positionHandlers: saved.positionHandlers,
                lineType: saved.lineType,
              }
            : edge.data,
        };
      }),
    );
    setPreLayoutSnapshot(null);
  };

  return {
    preLayoutSnapshot,
    setPreLayoutSnapshot,
    layoutPositionsRef,
    layoutEdgesRef,
    handleAutoLayout,
    handleRevertLayout,
  };
}
